import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HelpDrawer from "@/pages/admin/components/HelpDrawer";
import {
  adminNavFlat,
  adminNotifications,
  adminSearchIndex,
  findNavItem,
  quickCreateOptions,
  type AdminSearchHit,
} from "@/pages/admin/navConfig";

interface AdminTopHeaderProps {
  email?: string | null;
  onToggleSidebar: () => void;
  onSignOut: () => void;
  onNotify: (message: string) => void;
  pendingReviews: number;
}

type MenuKey = "search" | "create" | "notifications" | "profile" | null;

const NOTIF_TONE: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-900",
  secondary: "bg-secondary-100 text-secondary-700",
};

interface HeaderNotification {
  title: string;
  sub: string;
  icon: string;
  tone: "primary" | "accent" | "secondary";
  to?: string;
}

export default function AdminTopHeader({
  email,
  onToggleSidebar,
  onSignOut,
  onNotify,
  pendingReviews,
}: AdminTopHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState<MenuKey>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [query, setQuery] = useState("");
  const actionsRef = useRef<HTMLDivElement>(null);

  const match = findNavItem(location.pathname);

  useEffect(() => {
    setOpen(null);
    setQuery("");
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setOpen(null);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(null);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { pages: [], records: [] as AdminSearchHit[] };
    const pages = adminNavFlat
      .filter((item) => item.label.toLowerCase().includes(q))
      .slice(0, 5);
    const records = adminSearchIndex
      .filter((hit) =>
        `${hit.category} ${hit.label} ${hit.sub || ""}`.toLowerCase().includes(q),
      )
      .slice(0, 8);
    return { pages, records };
  }, [query]);

  const toggle = (key: Exclude<MenuKey, null>) =>
    setOpen((current) => (current === key ? null : key));

  const notifications = useMemo<HeaderNotification[]>(() => {
    const live: HeaderNotification[] =
      pendingReviews > 0
        ? [
            {
              title: "New review submitted",
              sub: `${pendingReviews} review${pendingReviews === 1 ? "" : "s"} awaiting approval`,
              icon: "ri-star-line",
              tone: "accent",
              to: "/admin/reviews",
            },
          ]
        : [];
    return [...live, ...adminNotifications];
  }, [pendingReviews]);

  const go = (to: string) => {
    setOpen(null);
    setQuery("");
    navigate(to);
  };

  const iconBtn =
    "relative w-10 h-10 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer";

  return (
    <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-6 lg:px-8 h-16 flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleSidebar}
          aria-label="Toggle navigation"
          className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer shrink-0"
        >
          <i className="ri-menu-2-line text-xl"></i>
        </button>

        <div className="min-w-0 hidden sm:block">
          <div className="flex items-center gap-1.5 text-[11px] text-foreground-500">
            <i className="ri-home-4-line"></i>
            <span>{match?.group.heading || "Admin"}</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700 font-medium">{match?.item.label || "Dashboard"}</span>
          </div>
          <p className="font-heading text-lg text-foreground-950 font-semibold truncate leading-tight mt-0.5">
            {match?.item.label || "Dashboard"}
          </p>
        </div>

        <div ref={actionsRef} className="ml-auto flex items-center gap-1 md:gap-1.5">
          {/* Global search */}
          <div className="relative hidden md:block">
            <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="search"
              value={query}
              onFocus={() => setOpen("search")}
              onChange={(event) => {
                setQuery(event.target.value);
                setOpen("search");
              }}
              placeholder="Search visitors, farms, bookings…"
              className="w-56 lg:w-72 bg-background-100 border border-background-300 rounded-md pl-10 pr-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />

            {open === "search" ? (
              <div className="absolute right-0 mt-2 w-[420px] max-w-[85vw] bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                {query.trim() === "" ? (
                  <p className="px-4 py-5 text-sm text-foreground-500">
                    Search across visitors, farmers, farms, bookings, experiences, transactions and content.
                  </p>
                ) : searchResults.pages.length === 0 && searchResults.records.length === 0 ? (
                  <p className="px-4 py-5 text-sm text-foreground-500">
                    No matches for “{query}”.
                  </p>
                ) : (
                  <div className="max-h-[70vh] overflow-y-auto py-1.5">
                    {searchResults.pages.length > 0 ? (
                      <div className="px-2">
                        <p className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground-500">
                          Go to
                        </p>
                        {searchResults.pages.map((page) => (
                          <button
                            key={page.to}
                            type="button"
                            onClick={() => go(page.to)}
                            className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-left hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                              <i className={`${page.icon} text-base`}></i>
                            </span>
                            <span className="text-sm text-foreground-900 truncate">{page.label}</span>
                            <i className="ri-arrow-right-line ml-auto text-foreground-400"></i>
                          </button>
                        ))}
                      </div>
                    ) : null}

                    {searchResults.records.length > 0 ? (
                      <div className="px-2 mt-1">
                        <p className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground-500">
                          Records
                        </p>
                        {searchResults.records.map((hit) => (
                          <button
                            key={`${hit.category}-${hit.label}`}
                            type="button"
                            onClick={() => go(hit.to)}
                            className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-left hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0">
                              <i className={`${hit.icon} text-base`}></i>
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm text-foreground-900 truncate">
                                {hit.label}
                              </span>
                              <span className="block text-xs text-foreground-500 truncate">
                                {hit.category}
                                {hit.sub ? ` · ${hit.sub}` : ""}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Quick Create */}
          <div className="relative">
            <button
              type="button"
              onClick={() => toggle("create")}
              className="inline-flex items-center gap-1.5 bg-primary-500 text-background-50 text-sm font-semibold pl-3.5 pr-3 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line text-lg"></i>
              <span className="hidden lg:inline">Quick Create</span>
            </button>

            {open === "create" ? (
              <div className="absolute right-0 mt-2 w-60 bg-background-50 border border-background-200 rounded-lg overflow-hidden py-1.5">
                {quickCreateOptions.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => go(option.to)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-900 shrink-0">
                      <i className={`${option.icon} text-base`}></i>
                    </span>
                    <span className="text-sm text-foreground-800">{option.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* Notifications */}
          <div className="relative">
            <button
              type="button"
              onClick={() => toggle("notifications")}
              aria-label="Notifications"
              className={iconBtn}
            >
              <i className="ri-notification-3-line text-xl"></i>
              <span className="absolute top-1.5 right-1.5 w-4 h-4 flex items-center justify-center rounded-full bg-accent-500 text-accent-950 text-[9px] font-bold">
                {notifications.length}
              </span>
            </button>

            {open === "notifications" ? (
              <div className="absolute right-0 mt-2 w-[340px] max-w-[85vw] bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
                  <p className="text-sm font-semibold text-foreground-950">Notifications</p>
                  <button
                    type="button"
                    onClick={() => onNotify("All notifications marked as read.")}
                    className="text-xs font-semibold text-primary-700 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    Mark all read
                  </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {notifications.map((note) => (
                    <button
                      key={note.title}
                      type="button"
                      onClick={() =>
                        note.to
                          ? go(note.to)
                          : onNotify("Notification details open in a later phase.")
                      }
                      className="w-full flex items-start gap-3 px-4 py-3 text-left border-b border-background-100 last:border-b-0 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span
                        className={`w-9 h-9 flex items-center justify-center rounded-full shrink-0 ${
                          NOTIF_TONE[note.tone]
                        }`}
                      >
                        <i className={`${note.icon} text-base`}></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-900">{note.title}</span>
                        <span className="block text-xs text-foreground-500 mt-0.5">{note.sub}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="px-4 py-2.5 border-t border-background-200">
                  <Link
                    to="/admin/notifications"
                    onClick={() => setOpen(null)}
                    className="text-xs font-semibold text-primary-700 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    View all notifications
                  </Link>
                </div>
              </div>
            ) : null}
          </div>

          {/* Help */}
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            aria-label="Help"
            className={iconBtn}
          >
            <i className="ri-question-line text-xl"></i>
          </button>

          {/* Profile */}
          <div className="relative">
            <button
              type="button"
              onClick={() => toggle("profile")}
              className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Admin profile"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-600 text-background-50 text-xs font-bold">
                SD
              </span>
              <span className="hidden lg:block text-left">
                <span className="block text-sm font-semibold text-foreground-900 leading-tight">
                  Suresh DNV
                </span>
                <span className="block text-[11px] text-foreground-500 leading-tight">
                  Super Admin
                </span>
              </span>
              <i className="ri-arrow-down-s-line text-foreground-400 hidden lg:block"></i>
            </button>

            {open === "profile" ? (
              <div className="absolute right-0 mt-2 w-64 bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-background-200">
                  <p className="text-sm font-semibold text-foreground-950">Suresh DNV</p>
                  <p className="text-xs text-foreground-500 truncate">{email || "Super Admin"}</p>
                </div>
                <div className="py-1.5">
                  {[
                    { label: "My Profile", icon: "ri-user-line" },
                    { label: "Preferences", icon: "ri-equalizer-line" },
                    { label: "Security", icon: "ri-shield-keyhole-line" },
                    { label: "Activity", icon: "ri-history-line" },
                  ].map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => {
                        setOpen(null);
                        onNotify(`${option.label} is coming in a later phase.`);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className={`${option.icon} text-foreground-500`}></i>
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="border-t border-background-200 py-1.5">
                  <button
                    type="button"
                    onClick={onSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line text-foreground-500"></i>
                    Logout
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </header>
  );
}